import { useCallback, useEffect, useRef, useState } from 'react';
import { getRunBySession } from '@/services/workflow/get-run-by-session';
import type { WorkflowRunWithResults } from '@/services/workflow/get-workflow-run';

/** `workflow_run.status` as the backend spells it (`RunStatus` in agentic-backend). */
export type RunStatus =
  | 'queued'
  | 'running'
  | 'paused'
  | 'success'
  | 'failed'
  | 'cancelled'
  | 'stopped';

const TERMINAL_STATUSES: RunStatus[] = [
  'success',
  'failed',
  'cancelled',
  'stopped',
];

export interface PollResult {
  status: RunStatus;
  isTerminal: boolean;
  errorMessage?: string | null;
  startedAt?: string | null;
  /** Full run snapshot when the poll returned a terminal status. */
  run?: WorkflowRunWithResults;
}

interface UseRunStatusPollerOptions {
  workflowId: string | undefined;
  sessionId: string | null;
  orgId: string | undefined;
  onTerminalStatus: (result: PollResult) => void;
  onPollError: (error: string) => void;
}

const MAX_POLL_ATTEMPTS = 20;
const POLL_INTERVAL_MS = 3000;

export function useRunStatusPoller({
  workflowId,
  sessionId,
  orgId,
  onTerminalStatus,
  onPollError,
}: UseRunStatusPollerOptions) {
  const [isPolling, setIsPolling] = useState(false);
  const [pollCount, setPollCount] = useState(0);
  const [pollExhausted, setPollExhausted] = useState(false);
  const [runStartedAt, setRunStartedAt] = useState<string | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pollCountRef = useRef(0);
  const stoppedRef = useRef(false);
  const onTerminalStatusRef = useRef(onTerminalStatus);
  const onPollErrorRef = useRef(onPollError);

  onTerminalStatusRef.current = onTerminalStatus;
  onPollErrorRef.current = onPollError;

  const stopPolling = useCallback((options?: { exhausted?: boolean }) => {
    stoppedRef.current = true;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsPolling(false);

    if (options?.exhausted) {
      setPollExhausted(true);
      pollCountRef.current = MAX_POLL_ATTEMPTS;
      setPollCount(MAX_POLL_ATTEMPTS);
      return;
    }

    setPollExhausted(false);
    pollCountRef.current = 0;
    setPollCount(0);
  }, []);

  const schedulePoll = useCallback(() => {
    if (stoppedRef.current) return;

    timeoutRef.current = setTimeout(async () => {
      if (stoppedRef.current) return;

      if (!workflowId || !sessionId || !orgId) {
        onPollErrorRef.current('Missing workflow, session, or org ID');
        stopPolling();
        return;
      }

      pollCountRef.current += 1;
      setPollCount(pollCountRef.current);

      if (pollCountRef.current >= MAX_POLL_ATTEMPTS) {
        onPollErrorRef.current(
          `Could not determine run status after ${MAX_POLL_ATTEMPTS} attempts`,
        );
        stopPolling({ exhausted: true });
        return;
      }

      try {
        const response = await getRunBySession(workflowId, sessionId, orgId);

        if (response.detail || !response.data) {
          console.warn(
            '[RunStatusPoller] API returned error:',
            response.detail,
          );
          schedulePoll();
          return;
        }

        const status = response.data.status.toLowerCase() as RunStatus;
        const isTerminal = TERMINAL_STATUSES.includes(status);

        // Polling may be stopped because the WS reconnected while this
        // request was in flight — still apply terminal snapshots.
        if (stoppedRef.current && !isTerminal) {
          return;
        }

        if (response.data.started_at) {
          setRunStartedAt(response.data.started_at);
        }

        if (isTerminal) {
          stopPolling();
          onTerminalStatusRef.current({
            status,
            isTerminal: true,
            errorMessage: response.data.error_message,
            startedAt: response.data.started_at,
            run: response.data,
          });
        } else if (!stoppedRef.current) {
          schedulePoll();
        }
      } catch (error) {
        console.error('[RunStatusPoller] Poll failed:', error);
        if (!stoppedRef.current) {
          schedulePoll();
        }
      }
    }, POLL_INTERVAL_MS);
  }, [workflowId, sessionId, orgId, stopPolling]);

  const startPolling = useCallback(() => {
    if (isPolling || pollExhausted) return;

    stoppedRef.current = false;
    setPollExhausted(false);
    pollCountRef.current = 0;
    setPollCount(0);
    setIsPolling(true);
    schedulePoll();
  }, [isPolling, pollExhausted, schedulePoll]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stoppedRef.current = true;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  return {
    isPolling,
    pollCount,
    pollExhausted,
    maxPollAttempts: MAX_POLL_ATTEMPTS,
    runStartedAt,
    startPolling,
    stopPolling,
  };
}
